import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import axios from "axios";
import Icon from '@mdi/react';
import { mdiPlus } from '@mdi/js';

function AddSponsorModal({ show, handleClose, onSponsorAdded, seasonId }) {
  const [formData, setFormData] = useState({
    name: "",
    tier: "",
    website: "",
    amount_contributed: "",
    description: "",
    is_active: true,
    display_on_website: true,
    logo: null,
  });

  const [seasonDetails, setSeasonDetails] = useState(null);
  const [sponsorTiers, setSponsorTiers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tiersLoading, setTiersLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const fetchSeasonDetails = async () => {
    try {
      const response = await axios.get(`/api/leagues/seasons/${seasonId}/`, {
        withCredentials: true,
      });

      if (response.status === 200) {
        setSeasonDetails(response.data);
      }
    } catch (error) {
      console.error('Error fetching season details:', error);
    }
  };

  const fetchSponsorTiers = async () => {
    setTiersLoading(true);
    try {
      const response = await axios.get(`/api/leagues/sponsor-tiers/`, {
        withCredentials: true,
      });

      if (response.status === 200) {
        setSponsorTiers(response.data);
      }
    } catch (error) {
      console.error('Error fetching sponsor tiers:', error);
    } finally {
      setTiersLoading(false);
    }
  };

  useEffect(() => {
    if (!show || !seasonId) return;
    fetchSeasonDetails();
    fetchSponsorTiers();
  }, [show, seasonId]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'file' ? files[0] : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessages([]);

    // Validate required fields
    const requiredFields = {
      name: 'Sponsor Name',
      tier: 'Sponsor Tier'
    };

    const missingFields = [];
    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        missingFields.push(label);
      }
    }

    if (missingFields.length > 0) {
      setErrorMessages([`Please fill in the following required fields: ${missingFields.join(', ')}`]);
      setLoading(false);
      return;
    }

    // Create FormData for file upload
    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('tier', formData.tier);
    submitData.append('season', seasonId);

    // Only append non-empty values
    if (formData.website && formData.website.trim() !== '') {
      let website = formData.website.trim();
      // Add https:// if no protocol is specified
      if (!website.startsWith('http://') && !website.startsWith('https://')) {
        website = 'https://' + website;
      }
      submitData.append('website', website);
    }
    if (formData.amount_contributed) {
      submitData.append('amount_contributed', formData.amount_contributed);
    }
    if (formData.description) {
      submitData.append('description', formData.description);
    }

    submitData.append('is_active', formData.is_active);
    submitData.append('display_on_website', formData.display_on_website);

    if (formData.logo) {
      submitData.append('logo', formData.logo);
    }

    axios
      .post("/api/leagues/sponsors/", submitData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      .then((response) => {
        onSponsorAdded(response.data);
        setFormData({
          name: "",
          tier: "",
          website: "",
          amount_contributed: "",
          description: "",
          is_active: true,
          display_on_website: true,
          logo: null,
        });
        handleClose();
      })
      .catch((error) => {
        const data = error.response?.data || {};

        if (typeof data === 'object') {
          const parts = Object.entries(data).flatMap(([field, msgs]) =>
            (Array.isArray(msgs) ? msgs : [msgs]).map(m =>
              `Field [${field}]: ${m}`
            )
          );
          setErrorMessages(parts);
        } else {
          setErrorMessages(["An unexpected error occurred. Please try again."]);
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <Icon path={mdiPlus} size={1} className="me-2" />
          Add Sponsor
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessages.map((err, i) => (
          <div key={i} className="text-danger mb-2">{err}</div>
        ))}
        <Form onSubmit={handleSubmit} noValidate>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Sponsor Name *</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter sponsor name"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Sponsor Tier *</Form.Label>
                <Form.Select
                  name="tier"
                  value={formData.tier}
                  onChange={handleChange}
                  required
                  disabled={tiersLoading}
                >
                  <option value="">Select a tier</option>
                  {sponsorTiers.map((tier) => (
                    <option key={tier.id} value={tier.id}>
                      {tier.display_name} - ${tier.price}
                    </option>
                  ))}
                </Form.Select>
                {tiersLoading && <Spinner animation="border" size="sm" className="mt-1" />}
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Website</Form.Label>
                <Form.Control
                  type="url"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Amount Contributed</Form.Label>
                <Form.Control
                  type="number"
                  name="amount_contributed"
                  value={formData.amount_contributed}
                  onChange={handleChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Enter sponsor description"
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Form.Group>
                <Form.Label>Logo</Form.Label>
                <Form.Control
                  type="file"
                  name="logo"
                  onChange={handleChange}
                  accept="image/*"
                />
                <Form.Text className="text-muted">
                  Upload a logo image (JPG, PNG, GIF)
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  label="Active Sponsor"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  name="display_on_website"
                  checked={formData.display_on_website}
                  onChange={handleChange}
                  label="Display on Website"
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            'Create Sponsor'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default AddSponsorModal; 